import { defineToolkit } from '@assistant-ui/react';
import { ChartToolUI } from './ChartToolUI';
import { PdfReportToolUI } from './PdfReportToolUI';
import { SkillOverviewToolUI } from './SkillOverviewToolUI';
import { ConfigureSkillToolUI } from './skill-flow/ConfigureSkillToolUI';
import { SkillExecutionToolUI } from './skill-flow/SkillExecutionToolUI';
import { BackgroundToolUI } from './skill-flow/BackgroundToolUI';
import { ConfigurePromptTemplateToolUI } from './skill-flow/ConfigurePromptTemplateToolUI';

/** Fallback human-in-the-loop configure tools (must match backend toolName). */
export const MCP_HUMAN_SKILL_TOOL_NAMES = [
  'configure_seo_content',
  'configure_seo_programmatic',
  'configure_seo_page',
  'configure_seo_technical',
  'configure_seo_schema',
  'configure_seo_images',
  'configure_seo_hreflang',
  'configure_seo_local',
  'configure_seo_maps',
  'configure_seo_geo',
  'configure_seo_ecommerce',
  'configure_seo_sitemap',
  'configure_seo_audit'
] as const;

export const MCP_PROMPT_TEMPLATE_TOOL_NAME = 'configure_prompt_template';

export const MCP_HUMAN_TOOL_NAMES = [
  ...MCP_HUMAN_SKILL_TOOL_NAMES,
  MCP_PROMPT_TEMPLATE_TOOL_NAME
] as const;

/** Backend convention: seo-audit → configure_seo_audit */
export function configureToolNameFromSkillCommand(command: string): string {
  return `configure_${command.trim().replace(/-/g, '_')}`;
}

/** Any chat configure_* tool is human-in-the-loop (skills + prompt templates). */
export function isMcpHumanToolName(toolName: string): boolean {
  return toolName.startsWith('configure_');
}

export function buildHumanToolNames(skillCommands: string[] = []): string[] {
  const fromSkills = skillCommands
    .map((command) => command.trim())
    .filter(Boolean)
    .map(configureToolNameFromSkillCommand);
  return [...new Set([...MCP_HUMAN_TOOL_NAMES, ...fromSkills])];
}

function skillConfigureToolkitEntries(toolNames: string[]) {
  return Object.fromEntries(
    toolNames
      .filter(
        (name) =>
          name.startsWith('configure_') &&
          name !== MCP_PROMPT_TEMPLATE_TOOL_NAME
      )
      .map((name) => [
        name,
        {
          type: 'human' as const,
          display: 'standalone' as const,
          render: ConfigureSkillToolUI
        }
      ])
  );
}

/**
 * Render-only toolkit for MCP backend tools + human-in-the-loop skill tools.
 * Pass skill commands from GET /chat/skills so new skills get a form without
 * another hard-coded allowlist update.
 */
export function buildMcpChatToolkit(skillCommands: string[] = []) {
  const humanNames = buildHumanToolNames(skillCommands);
  return defineToolkit({
    present_chart: {
      type: 'backend',
      display: 'standalone',
      render: ChartToolUI
    },
    present_skill_overview: {
      type: 'backend',
      display: 'standalone',
      render: SkillOverviewToolUI
    },
    render_seo_report_pdf: {
      type: 'backend',
      display: 'standalone',
      render: PdfReportToolUI
    },
    skill_execution: {
      type: 'backend',
      display: 'standalone',
      render: SkillExecutionToolUI
    },
    lighthouse_create: {
      type: 'backend',
      display: 'standalone',
      render: BackgroundToolUI
    },
    configure_prompt_template: {
      type: 'human',
      display: 'standalone',
      render: ConfigurePromptTemplateToolUI
    },
    ...skillConfigureToolkitEntries(humanNames)
  });
}

/** Static toolkit fallback before skills catalog is loaded. */
export const mcpChatToolkit = buildMcpChatToolkit();
